import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useInterval } from 'usehooks-ts';
import { Database } from '~/services/db';
import { Game } from '~/types/game';

import { PositionLabel } from '../label';
import styles from './Leaderboard.module.css';

export type LeaderBoardProps = {
    clientID: string;
    game: Game;
};

const NUM_DAILY_SCORES = 30;

export function LeaderBoard({ clientID, game }: LeaderBoardProps) {
    const { t } = useTranslation();
    const topElement = useRef<number>(0);
    const scrollRef = useRef<HTMLDivElement[]>([]);

    // API handles filtering to unique players, so we can request exactly what we need
    const highscores = Database.useHighscores(clientID, game.id, 3);
    const dailyHighscores = Database.useDailyHighscores(clientID, game.id, NUM_DAILY_SCORES);

    // Tester l'ÉLÉMENT, pas la longueur du tableau : React remet à `null` les refs des
    // lignes démontées sans raccourcir `scrollRef.current`. Quand les scores du jour se
    // réinitialisent au petit matin (30 lignes → moins), les cases de fin restent à `null`
    // alors que `length` vaut toujours 30 → l'intervalle plantait et le défilement
    // automatique du classement s'arrêtait (aéroport de Bordeaux, 10/09 05h29, Sentry
    // JAVASCRIPT-REACT-J : 6 occurrences).
    const scrollToSection = (id: number) => {
        scrollRef.current[id]?.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'smooth' });
    };

    // Boucler sur les lignes RÉELLEMENT rendues (`dailyHighscores.slice(1)`), pas sur
    // NUM_DAILY_SCORES : au petit matin il ne reste que quelques scores du jour, et viser
    // les index 0-29 laissait le carrousel immobile 26 secondes sur 30 — le classement
    // paraissait figé sur l'écran du client.
    const visibleRows = Math.max(dailyHighscores.length - 1, 0);

    useInterval(() => {
        if (visibleRows === 0) return;
        topElement.current = (topElement.current + 1) % visibleRows;
        scrollToSection(topElement.current);
    }, 1000);

    return (
        <div className={styles.container}>
            <div className={styles.sectionContainer}>
                <p className={styles.title}>{t('console.records.allTime')}</p>
                <div className={styles.podium}>
                    {highscores.length > 0 && (
                        <PositionLabel
                            className={styles.firstPosition}
                            position={1}
                            player={highscores[0]}
                            score={highscores[0].score}
                        />
                    )}
                    {highscores.length > 1 && (
                        <PositionLabel
                            className={styles.secondPosition}
                            position={2}
                            player={highscores[1]}
                            score={highscores[1].score}
                        />
                    )}
                    {highscores.length > 2 && (
                        <PositionLabel
                            className={styles.thirdPosition}
                            position={3}
                            player={highscores[2]}
                            score={highscores[2].score}
                        />
                    )}
                </div>
            </div>
            <div className={styles.sectionContainer}>
                <p className={styles.title}>{t('console.records.daily')}</p>
                {dailyHighscores.length > 0 && (
                    <PositionLabel
                        className={styles.firstDailyPosition}
                        position={1}
                        player={dailyHighscores[0]}
                        score={dailyHighscores[0].score}
                    />
                )}
                <div className={styles.ranking}>
                    {dailyHighscores.slice(1).map((player: any, index: number) => (
                        <PositionLabel
                            ref={(ref) => {
                                (scrollRef.current as any)[index] = ref;
                            }}
                            key={`${player.uid}-${player.score}`}
                            position={index + 2}
                            player={player}
                            score={player.score}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
